import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import { CartProvider, useCart } from "./lib/cart";
import { http } from "./lib/api";
import { getSessionId, getUTM } from "./lib/utils";
import { useAddBox, usePublicSettings } from "./lib/goldenHourData";

import GoldenShell from "./components/GoldenShell";
import HomeGoldenHour from "./pages/HomeGoldenHour";
import { Toaster } from "sonner";

// Storefront routes — split out of the initial bundle.
const ShopGoldenHour = lazy(() => import("./pages/ShopGoldenHour"));
const GiftBoxesGoldenHour = lazy(() => import("./pages/GiftBoxesGoldenHour"));
const FestivalsGoldenHour = lazy(() => import("./pages/FestivalsGoldenHour"));
const ProductGoldenHour = lazy(() => import("./pages/ProductGoldenHour"));
const CheckoutGoldenHour = lazy(() => import("./pages/CheckoutGoldenHour"));
const GiftFinderGoldenHour = lazy(() => import("./pages/FlowsGoldenHour").then((m) => ({ default: m.GiftFinderGoldenHour })));
const BulkGoldenHour = lazy(() => import("./pages/FlowsGoldenHour").then((m) => ({ default: m.BulkGoldenHour })));
const TrackGoldenHour = lazy(() => import("./pages/FlowsGoldenHour").then((m) => ({ default: m.TrackGoldenHour })));
const ConfirmationGoldenHour = lazy(() => import("./pages/FlowsGoldenHour").then((m) => ({ default: m.ConfirmationGoldenHour })));
const KitsGoldenHour = lazy(() => import("./pages/InfoPagesGoldenHour").then((m) => ({ default: m.KitsGoldenHour })));
const StudioGoldenHour = lazy(() => import("./pages/InfoPagesGoldenHour").then((m) => ({ default: m.StudioGoldenHour })));
const PromiseGoldenHour = lazy(() => import("./pages/InfoPagesGoldenHour").then((m) => ({ default: m.PromiseGoldenHour })));
const MarkdownPolicyPage = lazy(() => import("./pages/PolicyPage"));
const NotFoundGoldenHour = lazy(() => import("./pages/InfoPagesGoldenHour").then((m) => ({ default: m.NotFoundGoldenHour })));
const ContactGoldenHour = lazy(() => import("./pages/InfoPagesGoldenHour").then((m) => ({ default: m.ContactGoldenHour })));

// Admin — kept entirely out of the storefront bundle.
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const Products = lazy(() => import("./pages/admin/Products"));
const Contacts = lazy(() => import("./pages/admin/Contacts"));
const Pipeline = lazy(() => import("./pages/admin/Pipeline"));
const Reviews = lazy(() => import("./pages/admin/Reviews"));
const BulkImages = lazy(() => import("./pages/admin/BulkImages"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const FollowUps = lazy(() => import("./pages/admin/FollowUps"));
const LaunchReadiness = lazy(() => import("./pages/admin/LaunchReadiness"));

function Fallback() {
  return <div style={{ minHeight: "60vh" }} aria-busy="true" />;
}

function Storefront({ settings }) {
  const addBox = useAddBox();
  const onKitsLead = (phone) => http.post("/newsletter/subscribe", { phone: String(phone || "").replace(/\D/g, ""), source: "kits" }).catch(() => {});
  return (
    <GoldenShell settings={settings}>
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/" element={<HomeGoldenHour onAdd={addBox} onKitsLead={onKitsLead} />} />
          <Route path="/shop" element={<ShopGoldenHour />} />
          <Route path="/gift-boxes" element={<GiftBoxesGoldenHour onAdd={addBox} />} />
          <Route path="/festivals" element={<FestivalsGoldenHour />} />
          <Route path="/kits" element={<KitsGoldenHour />} />
          <Route path="/product/:sku" element={<ProductGoldenHour />} />
          <Route path="/checkout" element={<CheckoutGoldenHour />} />
          <Route path="/order/:id" element={<ConfirmationGoldenHour />} />
          <Route path="/track" element={<TrackGoldenHour />} />
          <Route path="/gift-finder" element={<GiftFinderGoldenHour />} />
          <Route path="/bulk" element={<BulkGoldenHour />} />
          <Route path="/trio" element={<Navigate to="/gift-boxes" replace />} />
          <Route path="/our-studio" element={<StudioGoldenHour />} />
          <Route path="/about" element={<Navigate to="/our-studio" replace />} />
          <Route path="/re-pour-promise" element={<PromiseGoldenHour />} />
          <Route path="/guarantee" element={<PromiseGoldenHour />} />
          <Route path="/shipping" element={<MarkdownPolicyPage pageKey="shipping" title="Shipping & Delivery" description="How Little Oak ships hand-poured gifts across India: charges, dispatch times and delivery estimates." toc />} />
          <Route path="/refund-policy" element={<MarkdownPolicyPage pageKey="refund" title="Cancellation & Refund Policy" description="Little Oak's cancellation, refund, damage and 7-day exchange policy for hand-poured pieces." toc />} />
          <Route path="/privacy" element={<MarkdownPolicyPage pageKey="privacy" title="Privacy Policy" description="What data Little Oak collects, why, who we share it with, and your rights under the DPDP Act, 2023." toc />} />
          <Route path="/terms" element={<MarkdownPolicyPage pageKey="terms" title="Terms & Conditions" description="The terms for buying hand-poured candles, holders and gifts from Little Oak." toc />} />
          <Route path="/returns" element={<Navigate to="/refund-policy" replace />} />
          <Route path="/cancellation" element={<Navigate to="/refund-policy" replace />} />
          <Route path="/cancellation-refund" element={<Navigate to="/refund-policy" replace />} />
          <Route path="/refunds" element={<Navigate to="/refund-policy" replace />} />
          <Route path="/contact" element={<ContactGoldenHour />} />
          <Route path="*" element={<NotFoundGoldenHour />} />
        </Routes>
      </Suspense>
    </GoldenShell>
  );
}

function TrackPageView() {
  const loc = useLocation();
  const { track } = useCart();
  useEffect(() => { track("page_view", { url: loc.pathname }); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function App() {
  const settings = usePublicSettings();
  useEffect(() => { getSessionId(); getUTM(); }, []);

  return (
    <BrowserRouter>
      <CartProvider>
        <Toaster position="top-center" richColors />
        <TrackPageView />
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="launch-readiness" element={<LaunchReadiness />} />
              <Route path="follow-ups" element={<FollowUps />} />
              <Route path="orders" element={<Orders settings={settings} />} />
              <Route path="products" element={<Products />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="images" element={<BulkImages />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="/*" element={<Storefront settings={settings} />} />
          </Routes>
        </Suspense>
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
